import React, { useEffect, useState } from 'react';
import { MDBCol, MDBRow } from 'mdb-react-ui-kit';
import Swal from 'sweetalert2';
import axios from '../../../utils/axiosConfig';
import 'boxicons/css/boxicons.min.css';

const normalizeRoles = (payload) => {
    const source = Array.isArray(payload)
        ? payload
        : payload?.recordset || payload?.rows || payload?.roles || [];

    return source
        .map((role) => {
            if (typeof role === 'string' || typeof role === 'number') {
                const value = String(role);
                return { value, label: value };
            }

            const value =
                role?.role_id ??
                role?.RoleId ??
                role?.IDRol ??
                role?.id ??
                role?.ID ??
                role?.user_type ??
                role?.role ??
                role?.NombreRol ??
                role?.name ??
                role?.descripcion ??
                role?.Descripcion ??
                role?.value;
            const label =
                role?.NombreRol ??
                role?.name ??
                role?.descripcion ??
                role?.Descripcion ??
                role?.label ??
                role?.role ??
                role?.user_type ??
                value;

            return value == null ? null : { value: String(value), label: String(label) };
        })
        .filter(Boolean);
};

const FormModalCreateUsuarioStreaming = ({ onClose, selectedUsuario, listProyectos = [], listEmpresas = [] }) => {
    const isEdit = Boolean(selectedUsuario);

    const [username, setUsername]           = useState('');
    const [principalUser, setPrincipalUser] = useState(false);
    const [password, setPassword]           = useState('');
    const [showPass, setShowPass]           = useState(false);
    const [balance, setBalance]             = useState('');
    const [proyectoId, setProyectoId]       = useState('');
    const [companyId, setCompanyId]         = useState('');
    const [userType, setUserType]           = useState('');
    const [userTypes, setUserTypes]         = useState([]);
    const [rolesLoading, setRolesLoading]   = useState(false);
    const [rolesError, setRolesError]       = useState('');
    const [isActive, setIsActive]           = useState(true);
    const [loading, setLoading]             = useState(false);

    // Empresas filtradas por proyecto seleccionado
    const empresasFiltradas = listEmpresas.filter(
        (e) => !proyectoId || String(e.__proyectoId) === String(proyectoId)
    );

    // Populate form on edit
    useEffect(() => {
        if (selectedUsuario) {
            setPrincipalUser(Boolean(selectedUsuario.principal_user ?? selectedUsuario.ppal_user));
            setUsername(selectedUsuario.username || selectedUsuario.userName || '');
            setPassword('');
            setBalance(String(selectedUsuario.balance ?? ''));
            setProyectoId(String(selectedUsuario.project ?? ''));
            setCompanyId(String(selectedUsuario.company ?? ''));
            setUserType(String(selectedUsuario.role_id ?? selectedUsuario.RoleId ?? selectedUsuario.user_type ?? ''));
            setIsActive(selectedUsuario.is_active !== false);
            return;
        }

        setPrincipalUser(false);
        setUsername('');
        setPassword('');
        setBalance('');
        setProyectoId('');
        setCompanyId('');
        setUserType('');
        setIsActive(true);
    }, [selectedUsuario]);

    useEffect(() => {
        let active = true;

        const fetchUserTypes = async () => {
            setRolesLoading(true);
            setRolesError('');

            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_SERVER}/operaciones2/obtenerrolesapp`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                        },
                        withCredentials: true,
                    }
                );

                if (res?.data?.success === false) {
                    throw new Error(res?.data?.message || 'No se pudieron cargar los tipos de usuario.');
                }

                const roles = normalizeRoles(res?.data?.data ?? res?.data);
                console.log('Fetched user types:', roles);
                if (!active) return;

                setUserTypes(roles);
                setUserType((current) => {
                    if (current) return current;
                    return selectedUsuario ? '' : (roles[0]?.value || '');
                });
            } catch (error) {
                if (!active) return;
                setUserTypes([]);
                setRolesError(error?.message || 'No se pudieron cargar los tipos de usuario.');
            } finally {
                if (active) setRolesLoading(false);
            }
        };

        fetchUserTypes();
        return () => {
            active = false;
        };
    }, [selectedUsuario]);

    const handleProyectoChange = (e) => {
        setProyectoId(e.target.value);
        setCompanyId('');
    };

    const validate = () => {
        if (!username.trim()) {
            Swal.fire('Error', 'El nombre de usuario es requerido.', 'error');
            return false;
        }
        if (!isEdit && password.length < 6) {
            Swal.fire('Error', 'La contraseña debe tener al menos 6 caracteres.', 'error');
            return false;
        }
        if (isEdit && password && password.length < 6) {
            Swal.fire('Error', 'Si cambia la contraseña, debe tener al menos 6 caracteres.', 'error');
            return false;
        }
        if (balance === '' || isNaN(Number(balance))) {
            Swal.fire('Error', 'El balance debe ser un número válido.', 'error');
            return false;
        }
        if (!proyectoId) {
            Swal.fire('Error', 'Debe seleccionar un proyecto.', 'error');
            return false;
        }
        if (!companyId) {
            Swal.fire('Error', 'Debe seleccionar una empresa.', 'error');
            return false;
        }
        if (!userType) {
            Swal.fire('Error', 'Debe seleccionar un tipo de usuario.', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const normalizedRoleId = Number(userType);
        const payload = {
            username: username.trim(),
            balance: Number(balance),
            company: Number(companyId),
            project: Number(proyectoId),
            role_id: Number.isFinite(normalizedRoleId) ? normalizedRoleId : userType,
            principal_user: principalUser,
        };
        console.log('Payload before password and is_active:', payload);

        if (!isEdit || password) {
            payload.password = password;
        }

        if (isEdit) {
            payload.is_active = isActive;
        }

        setLoading(true);
        try {
        const usuarioId = selectedUsuario?.id || selectedUsuario?.IdUsuario || selectedUsuario?.ID;

        const url = isEdit
            ? `${process.env.REACT_APP_SERVER}/operaciones2/actualizarusuarioapp/${usuarioId}`
            : `${process.env.REACT_APP_SERVER}/operaciones2/crearusuarioapp`;

        const method = isEdit ? 'patch' : 'post';
        console.log(`Sending ${method.toUpperCase()} request to ${url} with payload:`, payload);

            const res = await axios[method](url, JSON.stringify(payload), {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                },
                withCredentials: true,
            });

            if (res?.data?.success === false) {
                Swal.fire('Error', res?.data?.message || 'No se pudo completar la operación.', 'error');
                return;
            }

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: isEdit ? 'Usuario actualizado correctamente.' : 'Usuario creado correctamente.',
                showConfirmButton: false,
                timer: 2000,
            });

            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || 'Error de red. Intente nuevamente.';
            Swal.fire('Error', msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            {/* Fila 1: Username + User Type */}
            <MDBRow className="mb-3 g-3">
                <MDBCol size="12" md="6">
                    <label className="form-label fw-semibold">
                        Usuario <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: ed.navarro"
                        value={username}
                        maxLength={50}
                        onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                        style={{ boxSizing: 'border-box' }}
                    />
                    <small className="text-muted">Nickname sin espacios. Ej: <i>ed.navarro</i></small>
                </MDBCol>

                <MDBCol size="12" md="6">
                    <label className="form-label fw-semibold">
                        Tipo de usuario <span className="text-danger">*</span>
                    </label>
                    <select
                        className="form-control"
                        value={userType}
                        onChange={(e) => setUserType(e.target.value)}
                        disabled={rolesLoading}
                    >
                        <option value="">
                            {rolesLoading ? 'Cargando tipos de usuario...' : 'Selecciona un tipo de usuario'}
                        </option>
                        {userType && !userTypes.some((role) => role.value === userType) && (
                            <option value={userType}>{userType}</option>
                        )}
                        {userTypes.map((role) => (
                            <option key={role.value} value={role.value}>
                                {role.label}
                            </option>
                        ))}
                    </select>
                    {rolesError && <small className="text-danger">{rolesError}</small>}
                </MDBCol>
            </MDBRow>

            {/* Fila 2: Password + Balance */}
            <MDBRow className="mb-3 g-3">
                <MDBCol size="12" md="6">
                    <label className="form-label fw-semibold">
                        Contraseña{' '}
                        {isEdit
                            ? <small className="text-muted fw-normal">(dejar en blanco para no cambiar)</small>
                            : <span className="text-danger">*</span>}
                    </label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type={showPass ? 'text' : 'password'}
                            className="form-control"
                            placeholder={isEdit ? 'Nueva contraseña (opcional)' : 'Mínimo 6 caracteres'}
                            value={password}
                            maxLength={64}
                            onChange={(e) => setPassword(e.target.value)}
                            style={{ boxSizing: 'border-box', paddingRight: '42px' }}
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPass((v) => !v)}
                            title={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                            style={{
                                position: 'absolute',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#6c757d',
                                fontSize: '20px',
                                padding: 0,
                                lineHeight: 1,
                            }}
                        >
                            <i className={`bx ${showPass ? 'bx-hide' : 'bx-show'}`} />
                        </button>
                    </div>
                    {password.length > 0 && password.length < 6 && (
                        <small className="text-danger">Mínimo 6 caracteres.</small>
                    )}
                </MDBCol>

                <MDBCol size="12" md="6">
                    <label className="form-label fw-semibold">
                        Balance <span className="text-danger">*</span>
                    </label>
                    <input
                        type="number"
                        className="form-control"
                        placeholder="Ej: 100"
                        value={balance}
                        min={0}
                        onChange={(e) => setBalance(e.target.value)}
                        style={{ boxSizing: 'border-box' }}
                    />
                </MDBCol>
            </MDBRow>

            {/* Fila 3: Proyecto + Empresa */}
            <MDBRow className="mb-3 g-3">
                <MDBCol size="12" md="6">
                    <label className="form-label fw-semibold">
                        Proyecto <span className="text-danger">*</span>
                    </label>
                    <select
                        className="form-control"
                        value={proyectoId}
                        onChange={handleProyectoChange}
                    >
                        <option value="">Selecciona un proyecto</option>
                        {listProyectos.map((p) => {
                            const id   = p?.IdProyecto ?? p?.IDProyecto ?? p?.id ?? p?.project;
                            const name = p?.NombreProyecto ?? p?.Proyecto ?? p?.project_name ?? p?.descripcion ?? p?.Descripcion;
                            return <option key={id} value={id}>{name}</option>;
                        })}
                    </select>
                </MDBCol>

                <MDBCol size="12" md="6">
                    <label className="form-label fw-semibold">
                        Empresa <span className="text-danger">*</span>
                    </label>
                    <select
                        className="form-control"
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        disabled={!proyectoId}
                    >
                        <option value="">
                            {proyectoId ? 'Selecciona una empresa' : 'Primero selecciona un proyecto'}
                        </option>
                        {empresasFiltradas.map((emp) => {
                            const id   = emp?.IdEmpresa ?? emp?.IDEmpresa ?? emp?.id ?? emp?.company;
                            const name = emp?.NombreEmpresa ?? emp?.Empresa ?? emp?.empresa ?? emp?.descripcion ?? emp?.Descripcion;
                            return <option key={id} value={id}>{name}</option>;
                        })}
                    </select>
                </MDBCol>
            </MDBRow>

            {/* Fila 4: Activo — solo en modo edición */}
            <MDBRow className="mb-3 g-3">
                <MDBCol size="12" md={isEdit ? '6' : '12'}>
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="switchPrincipalUserStreaming"
                            checked={principalUser}
                            onChange={(e) => setPrincipalUser(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="switchPrincipalUserStreaming">
                            {principalUser ? 'Usuario principal' : 'Usuario no principal'}
                        </label>
                    </div>
                </MDBCol>

                {isEdit && (
                    <MDBCol size="12" md="6">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="switchIsActiveStreaming"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                style={{ cursor: 'pointer' }}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="switchIsActiveStreaming">
                                {isActive ? 'Usuario Activo' : 'Usuario Inactivo'}
                            </label>
                        </div>
                    </MDBCol>
                )}
            </MDBRow>

            {/* Botones */}
            <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="btn-primary-modals-maestros"
                    disabled={loading}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            &nbsp;Guardando...
                        </>
                    ) : (
                        <>
                            <i className="bx bx-save" />
                            {isEdit ? 'Actualizar' : 'Crear Usuario'}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default FormModalCreateUsuarioStreaming;
